import React, { useState } from 'react';
import { normalizeTierName, openUpgradeCheckout } from '../../services/licensing';
import SignupForm from '../Auth/SignupForm';
import LoginForm from '../Auth/LoginForm';
import ForgotPassword from '../Auth/ForgotPassword';
import AuthAccessExperience, { AccountStartChoice } from '../Auth/AuthAccessExperience';
import { useAuth } from '../../hooks/useAuth';
import LandingNavigation from './sections/LandingNavigation';
import LandingHero from './sections/LandingHero';
import LandingLifeCategories from './sections/LandingLifeCategories';
import LandingPricing from './sections/LandingPricing';
import LandingHowItWorks from './sections/LandingHowItWorks';
import LandingAISection from './sections/LandingAISection';
import LandingPrivacySection from './sections/LandingPrivacySection';
import LandingDownloadsSection from './sections/LandingDownloadsSection';
import LandingFinalCTA from './sections/LandingFinalCTA';
import LandingFooter from './sections/LandingFooter';
import pb from '../../services/pocketbase';
import '../../styles/landing.css';
import { useUI } from '../../contexts/UIContext';

const PENDING_UPGRADE_TIER_KEY = 'thryveloop_pending_upgrade_tier_v1';
const SUPPORTED_UPGRADE_TIERS = new Set(['believer', 'guardian', 'lifetime']);

const LandingPage = ({ onGetStarted, onScienceClick, onHelpClick, onGuideClick, onResourcesClick, onKnowledgeGraphClick, onAboutClick, onPrivacyClick, onTermsClick }) => {
  const { signUp, signIn, resetPassword, isAuthenticated, signInWithGoogle } = useAuth();
  const { darkMode, setDarkMode, setThemePreferenceForScope, setThemeScope } = useUI();

  const readPendingUpgradeTier = () => {
    try {
      const raw = (localStorage.getItem(PENDING_UPGRADE_TIER_KEY) || '').toString().trim();
      const normalized = normalizeTierName(raw || '');
      return SUPPORTED_UPGRADE_TIERS.has(normalized) ? normalized : '';
    } catch (_) {
      return '';
    }
  };

  const [authView, setAuthView] = useState('landing');
  const [pendingUpgradeTier, setPendingUpgradeTier] = useState(() => readPendingUpgradeTier());
  const [checkoutPendingTier, setCheckoutPendingTier] = useState('');
  const [signupIntent, setSignupIntent] = useState('web');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const persistPendingUpgradeTier = React.useCallback((tier) => {
    const normalized = normalizeTierName(tier || '');
    if (!SUPPORTED_UPGRADE_TIERS.has(normalized)) return;
    setPendingUpgradeTier(normalized);
    try {
      localStorage.setItem(PENDING_UPGRADE_TIER_KEY, normalized);
    } catch (_) {
      // ignore
    }
  }, []);

  const clearPendingUpgradeTier = React.useCallback(() => {
    setPendingUpgradeTier('');
    try {
      localStorage.removeItem(PENDING_UPGRADE_TIER_KEY);
    } catch (_) {
      // ignore
    }
  }, []);

  const openAccountStartChoice = React.useCallback(() => {
    clearPendingUpgradeTier();
    setSignupIntent('web');
    setError(null);
    setAuthView('signup-choice');
  }, [clearPendingUpgradeTier]);

  const openCheckoutForTier = React.useCallback(async (targetTier) => {
    const tier = normalizeTierName(targetTier || '');
    if (!SUPPORTED_UPGRADE_TIERS.has(tier) || checkoutPendingTier) return;

    setCheckoutPendingTier(tier);
    const result = await openUpgradeCheckout({
      targetTier: tier,
      email: pb.authStore.model?.email || '',
      userId: pb.authStore.model?.id || ''
    });

    if (!result.redirecting) {
      setCheckoutPendingTier('');
      setError(result.error || 'We could not open secure checkout. Please try again.');
    }
  }, [checkoutPendingTier]);

  const handleUpgradeIntent = React.useCallback((targetTier) => {
    const tier = normalizeTierName(targetTier || '');
    if (!SUPPORTED_UPGRADE_TIERS.has(tier)) return;
    if (isAuthenticated) {
      openCheckoutForTier(tier);
      return;
    }
    persistPendingUpgradeTier(tier);
    setSignupIntent(tier === 'lifetime' ? 'lifetime' : 'web');
    setError(null);
    setAuthView('signup');
  }, [isAuthenticated, openCheckoutForTier, persistPendingUpgradeTier]);

  React.useEffect(() => {
    if (!isAuthenticated || !onGetStarted) return;
    const pendingTier = pendingUpgradeTier || readPendingUpgradeTier();
    if (pendingTier) {
      clearPendingUpgradeTier();
      window.setTimeout(() => {
        openCheckoutForTier(pendingTier);
      }, 100);
    }
    onGetStarted();
  }, [clearPendingUpgradeTier, isAuthenticated, onGetStarted, openCheckoutForTier, pendingUpgradeTier]);

  React.useEffect(() => {
    if (authView !== 'landing' && mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
    if (authView !== 'landing' && typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [authView, mobileMenuOpen]);

  React.useEffect(() => {
    setThemeScope('marketing');
  }, [setThemeScope]);

  const handleMarketingThemeToggle = React.useCallback(() => {
    const nextPreference = darkMode ? 'light' : 'dark';
    const nextIsDark = nextPreference === 'dark';
    setThemeScope('marketing');
    setThemePreferenceForScope('marketing', nextPreference);
    setDarkMode(nextIsDark);
    try {
      localStorage.setItem('thryveloop_theme_preference_marketing_v1', nextPreference);
      localStorage.setItem('thryveloop_theme_preference', nextPreference);
      localStorage.setItem('thryveloop_darkMode', String(nextIsDark));
      localStorage.setItem('thryveloop-theme', nextPreference);
    } catch (_) {
      // ignore localStorage write errors
    }
  }, [darkMode, setDarkMode, setThemePreferenceForScope, setThemeScope]);

  const handleGetDesktopApp = () => {
    // Always keep navigation in the current session/window.
    window.location.assign('/downloads/');
  };

  const handleSignup = async ({ email, password, name, captchaToken }) => {
    setIsLoading(true);
    setError(null);

    const { error: signupError } = await signUp({ email, password, name, captchaToken });

    if (signupError) {
      setError(signupError);
      setIsLoading(false);
      return;
    }

    setAuthView('verify-email');
    setIsLoading(false);
  };

  const handleLogin = async ({ email, password, rememberMe }) => {
    setIsLoading(true);
    setError(null);

    const { error: loginError } = await signIn({ email, password, rememberMe });

    if (loginError) {
      setError(loginError);
    }

    setIsLoading(false);
  };

  handleLogin.googleSignIn = async () => {
    setIsLoading(true);
    setError(null);
    const { error: loginError } = await signInWithGoogle();
    if (loginError) {
      setError(loginError);
    }
    setIsLoading(false);
  };

  const handleForgotPassword = async (email) => {
    setIsLoading(true);
    const result = await resetPassword(email);
    setIsLoading(false);
    return result;
  };

  const closeAuthExperience = () => {
    setAuthView('landing');
    setError(null);
  };

  const renderAuthExperience = (mode, content) => (
    <AuthAccessExperience
      mode={mode}
      darkMode={darkMode}
      onBack={closeAuthExperience}
      onOpenHelp={onGuideClick || onHelpClick}
      onThemeToggle={handleMarketingThemeToggle}
    >
      {content}
    </AuthAccessExperience>
  );

  if (authView === 'signup-choice') {
    return renderAuthExperience('signup-choice', (
      <AccountStartChoice
        onStartFree={() => {
          clearPendingUpgradeTier();
          setSignupIntent('web');
          setAuthView('signup');
        }}
        onStartLifetime={() => {
          persistPendingUpgradeTier('lifetime');
          setSignupIntent('lifetime');
          setAuthView('signup');
        }}
        onLogin={() => setAuthView('login')}
      />
    ));
  }

  if (authView === 'signup') {
    return renderAuthExperience('signup', (
      <SignupForm
        entryMode={signupIntent}
        onSignup={handleSignup}
        onSwitchToLogin={() => {
          setAuthView('login');
          setError(null);
        }}
        onNavigateToPrivacy={onPrivacyClick}
        onNavigateToTerms={onTermsClick}
        isLoading={isLoading}
        error={error}
      />
    ));
  }

  if (authView === 'login') {
    return renderAuthExperience('login', (
      <LoginForm
        onLogin={handleLogin}
        onSwitchToSignup={openAccountStartChoice}
        onForgotPassword={() => {
          setAuthView('forgot');
          setError(null);
        }}
        isLoading={isLoading}
        error={error}
      />
    ));
  }

  if (authView === 'forgot') {
    return renderAuthExperience('forgot', (
      <ForgotPassword
        onResetPassword={handleForgotPassword}
        onBackToLogin={() => {
          setAuthView('login');
          setError(null);
        }}
        isLoading={isLoading}
      />
    ));
  }

  if (authView === 'verify-email') {
    return renderAuthExperience('verify-email', (
      <div className="auth-form-container landing-verify-wrap">
        <div className="auth-form">
          <div className="auth-form-header">
            <h2>Check your email</h2>
            <p className="auth-form-subtitle">
              We sent a verification link to your email address.
            </p>
          </div>
          <div className="auth-success-banner">
            <p>
              <strong>Almost there.</strong> Open your inbox and verify your account to start using ThryveLoop.
            </p>
          </div>
          <button
            type="button"
            className="landing-btn landing-btn--primary landing-btn--full"
            onClick={() => setAuthView('login')}
          >
            Go to login
          </button>
        </div>
      </div>
    ));
  }

  return (
    <div className="landing-page">
      <a className="landing-skip-link" href="#main-content">Skip to content</a>

      <LandingNavigation
        onLoginClick={() => {
          setAuthView('login');
          setError(null);
        }}
        onSignupClick={() => {
          openAccountStartChoice();
        }}
        onResourcesClick={onResourcesClick || onHelpClick}
        onDownloadsClick={handleGetDesktopApp}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        darkMode={darkMode}
        onThemeToggle={handleMarketingThemeToggle}
      />

      <main id="main-content" className="landing-main">
        <LandingHero
          onSignup={openAccountStartChoice}
          onDesktop={handleGetDesktopApp}
        />

        <LandingLifeCategories />
        <LandingHowItWorks />
        <LandingAISection />
        <LandingPrivacySection onGetDesktopApp={handleGetDesktopApp} />

        <LandingPricing
          onSignup={openAccountStartChoice}
          onUpgradeBeliever={() => handleUpgradeIntent('believer')}
          onUpgradeGuardian={() => handleUpgradeIntent('guardian')}
          onUpgradeLifetime={() => handleUpgradeIntent('lifetime')}
          onGetDesktopApp={handleGetDesktopApp}
          isAuthenticated={isAuthenticated}
          checkoutPendingTier={checkoutPendingTier}
        />

        <LandingDownloadsSection onGetDesktopApp={handleGetDesktopApp} />

        <LandingFinalCTA
          onSignup={openAccountStartChoice}
          onUpgradeBeliever={() => handleUpgradeIntent('believer')}
          onUpgradeLifetime={() => handleUpgradeIntent('lifetime')}
          isAuthenticated={isAuthenticated}
          checkoutPendingTier={checkoutPendingTier}
        />
      </main>

      <LandingFooter
        onPrivacyClick={onPrivacyClick}
        onScienceClick={onScienceClick}
        onTermsClick={onTermsClick}
        onHelpClick={onHelpClick}
        onResourcesClick={onResourcesClick || onHelpClick}
        onKnowledgeGraphClick={onKnowledgeGraphClick || onResourcesClick || onHelpClick}
        onAboutClick={onAboutClick}
        onDownloadsClick={handleGetDesktopApp}
      />
    </div>
  );
};

export default LandingPage;
